import * as React from 'react';
import { useState } from 'react';
import './index.css';
import { ToastContainer } from 'react-toastify';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  Routes
} from 'react-router-dom';
import { ProtectedRoute } from './auth/protected-route';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpLink } from '@trpc/client';
import { trpc } from './utils/trpc-client';
import { Login } from './auth/oauth-login';
import superjson from 'superjson';
import { isLocal, isStaging, posthogApiKey, posthogConfig } from './utils/env-utils';
import { ChannelRatesContextProvider } from './features/strategy/channel-rates-context';
import { Sidebar } from './components/layout/sidebar';
import { AllPlatformBuys } from './features/media-buys/all-platform-buys';
import { StagingIndicator } from './components/feedback/staging-indicator';
import { showErrorToast } from './components/feedback/error-toast';
import { PreviousLocationProvider } from './utils/posthog/previous-location';
import posthog from 'posthog-js';
import { PostHogErrorBoundary, PostHogProvider } from 'posthog-js/react';
import { ErrorFallback } from './components/feedback/error-fallback';
import { AllCampaigns } from './features/zoho-campaigns/all-campaigns';
import { AllLineItems } from './features/line-items/all-line-items';
import { PageViewTracker } from './utils/posthog/page-view-tracker';
import { useCheckForNewVersion } from './components/feedback/update-available-toast';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { SparklinesModule } from '@ag-grid-enterprise/sparklines';
import { RichSelectModule } from '@ag-grid-enterprise/rich-select';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { CampaignPage } from '@/features/campaigns/campaign-page';
import { VizPage } from '@/features/ai/viz-page';
import { WrkthruPage } from '@/features/wrkthru/wrkthru-page';
import { useAssistantEnabled, useWrkthruEnabled } from '@/utils/feature-flags';
import { FreshnessIndicator } from '@/features/admin/freshness-indicator';
import { CompanionInstall } from '@/features/companion/CompanionInstall';
import { McpSetup } from '@/features/mcp/mcp-setup';
import { AdminSection } from '@/features/admin/admin-section';
import { ActivityPage } from '@/features/activity/ActivityPage';
import { TooltipProvider } from '@/components/forms/ui/tooltip';
import { AssistantPanel } from '@/features/ai/assistant-panel';
import { DevPanel } from '@/features/dev/dev-panel';

ModuleRegistry.registerModules([
  ServerSideRowModelModule,
  MenuModule,
  SetFilterModule,
  StatusBarModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  ClientSideRowModelModule,
  SparklinesModule,
  RichSelectModule,
  RowGroupingModule
]);

export function App() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: false, refetchOnWindowFocus: false },
          mutations: { retry: false }
        },
        queryCache: new QueryCache({
          onError: error => {
            posthog.captureException(error);
            showErrorToast();
          }
        }),
        mutationCache: new MutationCache({
          onError: error => posthog.captureException(error)
        })
      })
  );
  const [trpcClient] = useState(() => {
    return trpc.createClient({
      links: [httpLink({ url: '/api', transformer: superjson })]
    });
  });

  const router = createBrowserRouter(
    [
      {
        path: '/',
        element: <Root />,
        children: [
          {
            path: '/login',
            element: <Login />
          },
          {
            path: '/companion/install',
            element: (
              <ProtectedRoute>
                <CompanionInstall />
              </ProtectedRoute>
            )
          },
          {
            path: '/',
            element: (
              <ProtectedRoute>
                <InnerApp />
              </ProtectedRoute>
            ),
            index: true
          },
          {
            path: '*',
            element: (
              <ProtectedRoute>
                <InnerApp />
              </ProtectedRoute>
            )
          }
        ]
      }
    ],
    {
      /* Enable React Router v7 future flag to silence warnings:
          - relativeSplatPath: adopt v7 splat route behavior early
          https://reactrouter.com/v6/upgrading/future
      */
      future: { v7_relativeSplatPath: true }
    }
  );

  return (
    <PostHogProvider apiKey={posthogApiKey} options={posthogConfig}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <PostHogErrorBoundary fallback={<ErrorFallback />}>
            {/* Enable React Router v7 future flag to silence warnings:
                - startTransition: future-proof navigation updates
                https://reactrouter.com/v6/upgrading/future
            */}
            <RouterProvider router={router} future={{ v7_startTransition: true }} />
          </PostHogErrorBoundary>
        </QueryClientProvider>
      </trpc.Provider>
    </PostHogProvider>
  );
}

function Root() {
  return (
    <>
      <PageViewTracker />
      <Outlet />
    </>
  );
}

function InnerApp() {
  useCheckForNewVersion();
  const assistantEnabled = useAssistantEnabled();
  const wrkthruEnabled = useWrkthruEnabled();
  const { data: channels, isLoading: channelsIsLoading } = trpc.channels.useQuery();
  if (channelsIsLoading || !channels) {
    return <div>Loading....</div>;
  }

  return (
    <PreviousLocationProvider>
      <TooltipProvider delayDuration={300}>
        <ChannelRatesContextProvider channels={channels}>
          <div className="fixed flex h-lvh w-full">
            {isStaging && <StagingIndicator />}
            <Sidebar />
            <div className="relative flex min-w-0 flex-1 flex-col">
              <FreshnessIndicator />
              <div className="min-h-0 flex-1 overflow-y-auto">
                <Routes>
                  {wrkthruEnabled && <Route path="/wrkthru" element={<WrkthruPage />} />}
                  <Route path="/campaigns" element={<AllCampaigns />} />
                  <Route path="/line-items" element={<AllLineItems />} />
                  <Route path="/media-buys" element={<AllPlatformBuys />} />
                  <Route path="/campaigns/:zohoCampaignId">
                    <Route index element={<CampaignPage />} />
                    <Route path="*" element={<CampaignPage />} />
                  </Route>
                  <Route path="/activity" element={<ActivityPage />} />
                  <Route path="/admin/*" element={<AdminSection />} />
                  <Route path="/viz" element={<VizPage />} />
                  <Route path="/help/mcp-setup" element={<McpSetup />} />
                  <Route
                    path="*"
                    element={<Navigate to={wrkthruEnabled ? '/wrkthru' : '/campaigns'} />}
                  />
                </Routes>
              </div>
            </div>
            {/* Gated by `bravo-assistant-v1`. Wrkthru depends on the Assistant (every
                "Wrkthru It" launches it), so `useWrkthruEnabled` also requires this flag. */}
            {assistantEnabled && <AssistantPanel />}
            {isLocal && <DevPanel />}
            <ToastContainer
              autoClose={false}
              position="bottom-left"
              hideProgressBar
              closeButton={false}
              toastStyle={{ padding: 0, width: '500px', minHeight: 0 }}
            />
          </div>
        </ChannelRatesContextProvider>
      </TooltipProvider>
    </PreviousLocationProvider>
  );
}
